import React from 'react';
import { Alert } from '@mui/material';
import type { TeamAttendanceScope } from '../../store/slices/teamAttendanceSlice';
import { formatCount } from '../../utils/format';

/**
 * Tells an admin they are seeing every rep, not one team. That is the one
 * surprising scope; a manager's own team is already named in the header meta,
 * so it gets no callout. A note, not an alert: it is context, so it is not announced.
 */
export const ScopeNote: React.FC<{ scope: TeamAttendanceScope | undefined }> = ({ scope }) =>
  scope?.kind === 'all' ? (
    <Alert severity="info" role="note">
      {`Showing all active sales reps (${formatCount(scope.teamSize, 'rep')}) because you are signed in as an admin.`}
    </Alert>
  ) : null;

/**
 * The scope as page-header meta parts: "Your team · 8 reps". Until the server
 * says which scope applies, "Your team" holds the place so the line stays put.
 */
export const scopeMeta = (scope: TeamAttendanceScope | undefined) =>
  scope ? [scope.kind === 'all' ? 'All sales reps' : 'Your team', formatCount(scope.teamSize, 'rep')] : ['Your team'];

const statusOf = (error: unknown) => (error as { status?: unknown } | undefined)?.status;

/** A 403: the account has no team to show, so nothing on the page can load. */
export const isAccessError = (error: unknown) => statusOf(error) === 403;

/** Turns an RTK Query error into a sentence a manager can act on. */
export const describeQueryError = (error: unknown) => {
  const status = statusOf(error);
  if (status === 403) {
    return 'Your account is not linked to an active team. Ask an admin to check your employee record.';
  }
  if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
    return 'The attendance service didn’t respond. Check your connection, then try again.';
  }
  return 'The attendance service returned an error. Try again in a minute; if it keeps happening, tell an admin.';
};
